import { CommonModule } from '@angular/common';
import { Component, inject, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { NotificationService } from 'src/app/core';
import { Department, Machine } from 'src/app/modules/shared/interface/department.entity';
import { CreateShiftDTO, MachinesDTO, ShiftFormDTO, ShiftMachineTimeDTO, shiftDTO } from 'src/app/modules/shared/interface/shift.entity';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';
import { ModalService } from 'src/app/modules/shared/services/modal.service';
import { ShiftService } from 'src/app/modules/shared/services/shift.service';
import { ShiftTimeService } from 'src/app/modules/shared/services/shift-time.service';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';

@Component({
  selector: 'app-shift-management',
  imports: [
    CommonModule,
    FormsModule,
  ],
  templateUrl: './shift-management.component.html',
  styleUrl: './shift-management.component.scss'
})
export class ShiftManagementComponent {
  
  shiftService = inject(ShiftService);
  shiftTimeService = inject(ShiftTimeService);
  notification =  inject(NotificationService);
  departmentService = inject(DepartmentService);
  modal = inject(ModalService);
  globalDepartmentService = inject(GlobalDepartmentService);

  protected $$ShiftDTO = signal<shiftDTO[]>([]);
  protected $$departmentDTO = signal<Department[]>([]);
  protected $$availableMachines = signal<Machine[]>([]);
  protected $$ShiftCreateDTO = signal<CreateShiftDTO[]>([]);
  protected editShiftId = signal<number | null>(null);
  protected searchQuery = signal<string>('');
  protected selectedEditMachineId = signal<number | null>(null);
  protected machineEditForm = signal<{startTime: string, endTime: string}>({startTime: '', endTime: ''});
  protected originalMachineIds = signal<number[]>([]);
  protected isEditingNewMachines = signal<boolean>(false);
  protected originalShiftName = signal<string>('');

  protected canUpdateShift = computed(() => {
    if (this.editShiftId() === null) return true;
    const currentName = this.shiftForm().shift.trim();
    const origName = this.originalShiftName().trim();
    const isNameChanged = currentName.length > 0 && currentName !== origName;
    return isNameChanged || this.isEditingNewMachines();
  });

  protected filteredMachines = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const machines = this.$$availableMachines();
    if (!query) return machines;
    return machines.filter(m => m.machineName.toLowerCase().includes(query));
  });

  protected hasMachineTimeChanged = computed(() => {
    const id = this.selectedEditMachineId();
    if (!id) return false;
    const currentStartTime = this.getMachineTime(id, 'startTime');
    const currentEndTime = this.getMachineTime(id, 'endTime');
    const form = this.machineEditForm();
    return form.startTime !== currentStartTime || form.endTime !== currentEndTime;
  });

  protected getNewMachineNames = computed(() => {
    const originalIds = this.originalMachineIds();
    const currentMachines = this.shiftForm().machines;
    const newMachineIds = currentMachines.filter(m => !originalIds.includes(m.machineId)).map(m => m.machineId);
    
    const allMachines = this.$$availableMachines();
    const names = newMachineIds.map(id => allMachines.find(m => m.machineId === id)?.machineName).filter(n => !!n);
    return names.join(', ');
  });

  departmentId = this.globalDepartmentService.selectedDepartmentId;
  

  shiftForm = signal<ShiftFormDTO>({
    shift: '',
    startTime: '',
    endTime: '',
    departmentId: this.globalDepartmentService.getDepartmentId() ?? -1,
    isTemporary: false,
    machines: []
  });

  constructor() {
    this.fetchDepartments();
    this.getAllShifts();

  }

  fetchDepartments() {
    this.departmentService.getAllDepartments().subscribe({
      next: (result) => {
        if (result.data && result.data.departments) {
          this.$$departmentDTO.set(result.data.departments);
          if (this.departmentId()) {
            this.onDepartmentChange(this.departmentId()!);
          }
        }
      },
      error: (error) => {
        console.error('Error fetching departments:', error);
      }
    });
  }
  
  getAllShifts() {

    const id = this.departmentId();

    this.shiftService.getAll(id).subscribe({
      next: (result) => {
        if(result.data.shifts){
          this.$$ShiftDTO.set(result.data.shifts)          
        }
      },
      error: (error) => {
        console.error('Error fetching Operators:', error);
        this.notification.set({type: 'error', message: `Failed to Fetch Shifts.`});
      }
    });
  }

  onDepartmentChange(departmentId: number | string): void {
    const depId = Number(departmentId);
    this.globalDepartmentService.setDepartmentId(depId);

    const selectedDept = this.$$departmentDTO()
      .find(d => d.departmentId === depId);

    if (selectedDept) {
      this.$$availableMachines.set(selectedDept.machines);
    } else {
      this.$$availableMachines.set([]);
    }

    this.shiftForm.update(s => ({
      ...s,
      departmentId: depId,
      machines: []
    }));

    this.getAllShifts();
  }

  updateShiftName(name: string): void {
    this.shiftForm.update(form => ({ ...form, shift: name }));
  }

  openShiftAdd() {
    this.editShiftId.set(null);
    this.resetShiftForm();
    this.searchQuery.set('');
    this.modal.$$AddShiftModal.set(true);
  }

  onMachineToggle(machineId: number, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
 
    this.shiftForm.update(form => {
      let machines = [...form.machines];
      if (checked) {
        machines.push({ machineId, startTime: form.startTime || '', endTime: form.endTime || '' });
        // If in edit mode and it's a new machine, trigger the bulk edit panel
        if (this.editShiftId() !== null && !this.originalMachineIds().includes(machineId)) {
          this.isEditingNewMachines.set(true);
          this.selectedEditMachineId.set(null);
          // Initialize right side form with shift default times if empty
          if (!this.machineEditForm().startTime) {
            this.machineEditForm.set({ startTime: form.startTime, endTime: form.endTime });
          }
        }
      } else {
        machines = machines.filter((m: ShiftMachineTimeDTO) => m.machineId !== machineId);
        if (this.selectedEditMachineId() === machineId) {
          this.selectedEditMachineId.set(null);
        }
        // Check if we still have new machines
        if (this.editShiftId() !== null) {
          const originalIds = this.originalMachineIds();
          const hasNewMachines = machines.some(m => !originalIds.includes(m.machineId));
          if (!hasNewMachines) {
            this.isEditingNewMachines.set(false);
          }
        }
      }
      return { ...form, machines };
    });
  }
 
  isMachineSelected(machineId: number): boolean {
    return this.shiftForm().machines.some((m: ShiftMachineTimeDTO) => m.machineId === machineId);
  }

  getMachineTime(machineId: number, type: 'startTime' | 'endTime'): string {
    const machine = this.shiftForm().machines.find((m: ShiftMachineTimeDTO) => m.machineId === machineId);
    return machine ? machine[type] : '';
  }

  updateMachineTime(machineId: number, type: 'startTime' | 'endTime', value: string) {
    this.shiftForm.update(form => {
      const machines = form.machines.map((m: ShiftMachineTimeDTO) => {
        if (m.machineId === machineId) {
          return { ...m, [type]: value };
        }
        return m;
      });
      return { ...form, machines };
    });
  }

  selectMachineForEdit(machineId: number) {
    if (!this.isMachineSelected(machineId)) return;
    
    // Switch away from "new machines" mode when explicitly clicking a machine
    this.isEditingNewMachines.set(false);
    
    this.selectedEditMachineId.set(machineId);
    this.machineEditForm.set({
      startTime: this.getMachineTime(machineId, 'startTime'),
      endTime: this.getMachineTime(machineId, 'endTime')
    });
  }

  saveMachineTime() {
    const machineId = this.selectedEditMachineId();
    const shiftId = this.editShiftId();
    if (!machineId || !shiftId) return;

    const form = this.machineEditForm();
    if (!form.startTime || !form.endTime) {
      this.notification.set({ type: 'error', message: 'Please set both start and end time.' });
      return;
    }

    this.shiftTimeService.updateMachineTime({
      shiftId,
      machineId,
      startTime: form.startTime,
      endTime: form.endTime
    }).subscribe({
      next: () => {
        // Update the local form state to reflect saved values
        this.updateMachineTime(machineId, 'startTime', form.startTime);
        this.updateMachineTime(machineId, 'endTime', form.endTime);
        this.notification.set({ type: 'success', message: 'Machine time updated successfully!' });
      },
      error: (err) => {
        console.error(err);
        this.notification.set({ type: 'error', message: 'Failed to update machine time.' });
      }
    });
  }

  getSelectedMachineName() {
    const id = this.selectedEditMachineId();
    if (!id) return '';
    return this.$$availableMachines().find(m => m.machineId === id)?.machineName || '';
  }

  applyGlobalTimeToAllMachines() {
    const { startTime, endTime } = this.shiftForm();
    if (!startTime || !endTime) {
      this.notification.set({ type: 'error', message: 'Please set both Shift Start and End times first.' });
      return;
    }
    
    this.shiftForm.update(form => {
      const machines = form.machines.map((m: any) => ({
        ...m,
        startTime: startTime,
        endTime: endTime
      }));
      return { ...form, machines };
    });
    
    this.notification.set({ type: 'success', message: 'Global times applied to all machines.' });
  }
 

  saveShift(): void {
    if (this.editShiftId() !== null) {
      this.updateShift();
    } else {
      this.createShift();
    }
  }

  private formatTime(time: string): string {
    if (!time) return '';
    return time.length === 5 ? `${time}:00` : time;
  }

createShift(): void {

  const form = this.shiftForm();

  // Basic Validation
  if (
  !form.shift ||
  !form.startTime ||
  !form.endTime
) {
  this.notification.set({
    type: 'error',
    message: 'Please fill all required fields.'
  });
  return;
}

if (form.isTemporary && form.machines.length === 0) {
  this.notification.set({
    type: 'error',
    message: 'Please select at least one machine for temporary shift.'
  });
  return;
}

  const payload: CreateShiftDTO = {
    ...form,
    startTime: this.formatTime(form.startTime),
    endTime: this.formatTime(form.endTime),
    machines: form.isTemporary ? form.machines.map((m: ShiftMachineTimeDTO) => m.machineId) : []
  };

  this.shiftService.create(payload).subscribe({
    next: (res) => {

      this.notification.set({
        type: 'success',
        message: 'Shift Created Successfully!'
      });

      // Close Modal
      this.modal.$$AddShiftModal.set(false);

      // Reset Form
      this.resetShiftForm();

      // Refresh Table
      this.getAllShifts();
    },

    error: (err) => {
      console.error(err);

      this.notification.set({
        type: 'error',
        message: 'Failed to Create Shift.'
      });
    }
  });
}

  updateShift(): void {
    const form = this.shiftForm();
    const shiftId = this.editShiftId();
    const rightSideForm = this.machineEditForm();

    // Use the right-side panel's time if available, otherwise fallback to original shift time
    const putStartTime = rightSideForm.startTime || form.startTime;
    const putEndTime = rightSideForm.endTime || form.endTime;
 
    // if (!form.shift || !putStartTime || !putEndTime) {
    //   this.notification.set({ type: 'error', message: 'Please fill all required fields.' });
    //   return;
    // }
 
    if (form.isTemporary && form.machines.length === 0) {
      this.notification.set({ type: 'error', message: 'Please select at least one machine for temporary shift.' });
      return;
    }
 
    const updatePayload: CreateShiftDTO = {
      ...form,
      startTime: '',
      endTime: '',
      // startTime: this.formatTime(putStartTime),
      // endTime: this.formatTime(putEndTime),
      machines: form.isTemporary ? form.machines.map((m: ShiftMachineTimeDTO) => m.machineId) : []
    };

    this.shiftService.update(shiftId!, { ...updatePayload, isActive: 'active' }).subscribe({
      next: () => {
        const originalIds = this.originalMachineIds();
        const newMachineIds = form.machines
            .filter((m: ShiftMachineTimeDTO) => !originalIds.includes(m.machineId))
            .map((m: ShiftMachineTimeDTO) => m.machineId);

        if (this.isEditingNewMachines() && newMachineIds.length > 0 && putStartTime && putEndTime) {
            const updateObservables = newMachineIds.map(machineId => 
                this.shiftTimeService.updateMachineTime({
                    shiftId: shiftId!,
                    machineId: machineId,
                    startTime: putStartTime,
                    endTime: putEndTime
                })
            );

            forkJoin(updateObservables).subscribe({
                next: () => {
                    this.notification.set({ type: 'success', message: 'Shift and Machine Times Updated Successfully!' });
                    this.modal.$$AddShiftModal.set(false);
                    this.resetShiftForm();
                    this.editShiftId.set(null);
                    this.getAllShifts();
                },
                error: (err) => {
                    console.error(err);
                    this.notification.set({ type: 'error', message: 'Shift updated, but failed to update some machine times.' });
                    this.modal.$$AddShiftModal.set(false);
                    this.resetShiftForm();
                    this.editShiftId.set(null);
                    this.getAllShifts();
                }
            });
        } else {
            this.notification.set({ type: 'success', message: 'Shift Updated Successfully!' });
            this.modal.$$AddShiftModal.set(false);
            this.resetShiftForm();
            this.editShiftId.set(null);
            this.getAllShifts();
        }
      },
      error: (err) => {
        console.error(err);
        this.notification.set({ type: 'error', message: 'Failed to Update Shift.' });
      }
    });
  }


  resetShiftForm(): void {
    this.selectedEditMachineId.set(null);
    this.isEditingNewMachines.set(false);
    this.originalMachineIds.set([]);
    this.originalShiftName.set('');
    this.machineEditForm.set({startTime: '', endTime: ''});
    this.shiftForm.set({
      shift: '',
      startTime: '',
      endTime: '',
      departmentId: this.departmentId() ?? -1,
      isTemporary: false,
      machines: []
    });
  }

  openShiftEdit(shift: shiftDTO): void {
      // Ensure machines for this shift's department are loaded
      const dept = this.$$departmentDTO().find(d => d.departmentId === shift.departmentId);
      if (dept) {
        this.$$availableMachines.set(dept.machines);
      }
  
      // Populate the form with existing shift data
      this.shiftForm.set({
        shift: shift.shiftName,
        startTime: shift.startTime,
        endTime: shift.endTime,
        departmentId: shift.departmentId,
        isTemporary: !!shift.isTemporary,
        machines: shift.machines ? shift.machines.map((m: MachinesDTO) => ({
          machineId: m.machineId,
          startTime: m.startTime || shift.startTime,
          endTime: m.endTime || shift.endTime
        })) : []
      });
  
      this.editShiftId.set(shift.shiftId);
      this.originalShiftName.set(shift.shiftName || '');
      this.searchQuery.set('');
      this.selectedEditMachineId.set(null);
      this.isEditingNewMachines.set(false);
      this.originalMachineIds.set(shift.machines ? shift.machines.map((m: MachinesDTO) => m.machineId) : []);
      this.modal.$$AddShiftModal.set(true);
    }


}
